import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isPast } from 'date-fns';
import { 
  Flame, Clock, CheckCircle, Users, ChevronRight, 
  Calendar, Trophy, Loader2, Camera, Video, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PresignedImage, PresignedVideo } from '../components/PresignedMedia';
import { useAuth } from '../context/AuthContext';
import { timeAsync, timePageLoad } from '../utils/performanceLogger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SidequestsPage() {
  const { user, isAuthenticated } = useAuth();
  const [sidequests, setSidequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  
  // Page load timer
  const pageTimer = timePageLoad('SidequestsPage');

  useEffect(() => {
    fetchSidequests();
  }, []);
  
  // Mark page as ready when data is loaded
  useEffect(() => {
    if (!loading) {
      pageTimer.markReady({ questCount: sidequests.length });
    }
  }, [loading]);

  const fetchSidequests = async () => {
    try {
      const response = await timeAsync('API /sidequests', () => 
        axios.get(`${API}/sidequests`)
      );
      setSidequests(response.data.sidequests);
    } catch (error) {
      console.error('Failed to fetch sidequests:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeSidequests = sidequests.filter(sq => sq.status === 'active');
  const pastSidequests = sidequests.filter(sq => sq.status === 'closed' || (sq.status === 'active' && isPast(parseISO(sq.deadline))));

  const isDeadlinePassed = (deadline) => isPast(parseISO(deadline));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 rounded-full mb-4">
            <Flame className="w-5 h-5 text-orange-500" />
            <span className="text-orange-500 font-semibold">Weekly Challenges</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Sidequests</h1>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Complete weekly challengez, share your submissions, and compete with the community.
            New quests drop every Friday at 3pm EST!
          </p>
        </div>

        {/* User Stats (if logged in) */}
        {isAuthenticated && user?.sidequest_stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-4 text-center">
              <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{user.sidequest_stats.total_completed || 0}</p>
              <p className="text-xs text-neutral-400">Completed</p>
            </div>
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{user.sidequest_stats.on_time || 0}</p>
              <p className="text-xs text-neutral-400">On Time</p>
            </div>
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-4 text-center">
              <Clock className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{user.sidequest_stats.late || 0}</p>
              <p className="text-xs text-neutral-400">Late</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-[#121212] rounded-xl mb-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all
              ${activeTab === 'active' ? 'bg-red-500 text-white' : 'text-neutral-400 hover:text-white'}
            `}
          >
            Active ({activeSidequests.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all
              ${activeTab === 'past' ? 'bg-red-500 text-white' : 'text-neutral-400 hover:text-white'}
            `}
          >
            Past Quests
          </button>
        </div>

        {/* Active Sidequests */}
        {activeTab === 'active' && (
          <div className="space-y-4">
            {activeSidequests.length === 0 ? (
              <div className="text-center py-16">
                <Flame className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No active sidequests</h3>
                <p className="text-neutral-400">Check back Friday at 3pm EST for new challenges!</p>
              </div>
            ) : (
              activeSidequests.map(quest => (
                <ActiveQuestCard key={quest.id} quest={quest} isDeadlinePassed={isDeadlinePassed} />
              ))
            )}
          </div>
        )}

        {/* Past Sidequests */}
        {activeTab === 'past' && (
          <div className="space-y-4">
            {pastSidequests.length === 0 ? (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No past sidequests</h3>
                <p className="text-neutral-400">Completed quests will appear here.</p>
              </div>
            ) : (
              pastSidequests.map(quest => (
                <Link
                  key={quest.id}
                  to={`/sidequests/${quest.id}`}
                  className="block bg-[#121212] rounded-2xl border border-white/10 p-6 hover:border-white/20 transition-all group opacity-80"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {quest.user_submitted ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        ) : (
                          <Badge className="bg-neutral-500/20 text-neutral-400 border-neutral-500/30">
                            Missed
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{quest.title}</h3>
                      <p className="text-neutral-500 text-sm line-clamp-2 mb-4">{quest.description}</p>
                      <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(parseISO(quest.release_date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {quest.submission_count} submissions
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-6 h-6 text-neutral-600" />
                  </div>
                </Link>
              ))
            )}
          </div>
        )}

        {/* CTA for non-logged in users */}
        {!isAuthenticated && (
          <div className="mt-12 text-center p-8 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl border border-orange-500/20">
            <h3 className="text-xl font-bold text-white mb-2">Join the Community</h3>
            <p className="text-neutral-400 mb-4">Sign up to participate in sidequests and track your progress!</p>
            <Link to="/register">
              <Button className="bg-orange-500 hover:bg-orange-600 rounded-full px-8">
                Get Started
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Active quest card with featured auto-preview playback ---
function ActiveQuestCard({ quest, isDeadlinePassed }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const hasFeaturedVideo = quest.is_featured && quest.featured_video_url;

  useEffect(() => {
    if (!hasFeaturedVideo || !ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setVisible(entry.isIntersecting)),
      { threshold: 0.5 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasFeaturedVideo]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (visible) videoRef.current.play?.().catch(() => {});
    else videoRef.current.pause?.();
  }, [visible]);

  return (
    <Link
      ref={ref}
      to={`/sidequests/${quest.id}`}
      className="block bg-[#121212] rounded-2xl border border-white/10 overflow-hidden hover:border-orange-500/50 transition-all group"
      data-testid={`quest-card-${quest.id}`}
    >
      {/* Media preview (thumbnail/featured video) */}
      {(quest.thumbnail_url || hasFeaturedVideo) && (
        <div className="relative w-full h-56 bg-black overflow-hidden">
          {hasFeaturedVideo ? (
            <PresignedVideo
              ref={videoRef}
              src={quest.featured_video_url}
              className="w-full h-full object-cover"
              controls={false}
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : quest.thumbnail_url ? (
            <PresignedImage
              src={quest.thumbnail_url}
              alt={quest.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent pointer-events-none" />
          {quest.is_featured && (
            <Badge className="absolute top-3 left-3 bg-red-500 text-white border-red-500">
              <Star className="w-3 h-3 mr-1 fill-white" />
              Featured
            </Badge>
          )}
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {quest.user_submitted ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              ) : isDeadlinePassed(quest.deadline) ? (
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                  <Clock className="w-3 h-3 mr-1" />
                  Late Submission
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  <Flame className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              )}
              <div className="flex items-center gap-1 text-xs text-neutral-500">
                {quest.allow_photos && <Camera className="w-3 h-3" />}
                {quest.allow_videos && <Video className="w-3 h-3" />}
              </div>
            </div>
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">
              {quest.title}
            </h3>
            <p className="text-neutral-400 text-sm line-clamp-2 mb-4">{quest.description}</p>
            <div className="flex items-center gap-4 text-sm text-neutral-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Deadline: {format(parseISO(quest.deadline), 'MMM d, h:mm a')}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {quest.submission_count} submissions
              </span>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-neutral-600 group-hover:text-orange-400 transition-colors flex-shrink-0" />
        </div>
      </div>
    </Link>
  );
}
